/**
 * OccupationalRisk Filters Component
 * Inline filter bar: filters stay visible and apply live (no popover).
 */
import React from 'react';
import { InlineFilterBar, InlineSelect, InlineToggles } from '../../../components/common';
import { useFilterState } from '../../../hooks/useFilterState';
import { OccupationalRiskStatus, RiskType } from '../../../types/occupationalRisk';

export interface RiskFiltersData {
  searchTerm: string;
  status: string;
  riskType: string;
  unhealthyAgent: boolean;
  hazardousAgent: boolean;
  socialSecurityAgent: boolean;
  semCodigoTabela24: boolean;
}

interface Props {
  onFilter: (filters: RiskFiltersData) => void;
  loading?: boolean;
  total?: number;
}

const RISK_TYPE_LABELS: Record<string, string> = {
  [RiskType.PHYSICAL]: 'Físico',
  [RiskType.CHEMICAL]: 'Químico',
  [RiskType.BIOLOGICAL]: 'Biológico',
  [RiskType.ERGONOMIC]: 'Ergonômico',
  [RiskType.ACCIDENT]: 'Acidente',
  [RiskType.OTHER]: 'Outro',
};

const STATUS_OPTIONS = [
  { value: OccupationalRiskStatus.ACTIVE, label: 'Ativo' },
  { value: OccupationalRiskStatus.INACTIVE, label: 'Inativo' },
];

const RISK_TYPE_OPTIONS = Object.values(RiskType).map((t) => ({
  value: t,
  label: RISK_TYPE_LABELS[t],
}));

const defaultValues: RiskFiltersData = {
  searchTerm: '',
  status: '',
  riskType: '',
  unhealthyAgent: false,
  hazardousAgent: false,
  socialSecurityAgent: false,
  semCodigoTabela24: false,
};

export const OccupationalRiskFiltersComponent: React.FC<Props> = ({
  onFilter,
  loading = false,
  total,
}) => {
  const { filters, setFilter, clearAll, activeChips } = useFilterState(defaultValues, {
    onFilter,
    applyOnMount: true,
    live: true,
    labels: {
      status: 'Status',
      riskType: 'Risco',
    },
    formatValue: {
      status: (v: string) => (v === OccupationalRiskStatus.ACTIVE ? 'Ativo' : 'Inativo'),
      riskType: (v: string) => RISK_TYPE_LABELS[v] ?? v,
    },
  });

  return (
    <InlineFilterBar
      searchPlaceholder="Buscar por fator, agente, descrição..."
      searchValue={filters.searchTerm}
      onSearchChange={(v) => setFilter('searchTerm', v)}
      resultCount={total}
      resultNoun="riscos"
      chips={activeChips}
      onClearAll={clearAll}
      loading={loading}
    >
      <InlineSelect
        label="Status"
        value={filters.status}
        options={STATUS_OPTIONS}
        onChange={(v) => setFilter('status', v)}
        emptyLabel="Todos"
        minWidth={130}
      />
      <InlineSelect
        label="Tipo de Risco"
        value={filters.riskType}
        options={RISK_TYPE_OPTIONS}
        onChange={(v) => setFilter('riskType', v)}
        emptyLabel="Todos"
        minWidth={150}
      />
      <InlineToggles
        disabled={loading}
        items={[
          {
            label: 'Insalubre (NR-15)',
            checked: filters.unhealthyAgent,
            onChange: (v) => setFilter('unhealthyAgent', v),
          },
          {
            label: 'Perigoso (NR-16)',
            checked: filters.hazardousAgent,
            onChange: (v) => setFilter('hazardousAgent', v),
          },
          {
            label: 'Previdenciário',
            checked: filters.socialSecurityAgent,
            onChange: (v) => setFilter('socialSecurityAgent', v),
          },
          {
            label: 'Sem código da Tabela 24',
            checked: filters.semCodigoTabela24,
            onChange: (v) => setFilter('semCodigoTabela24', v),
          },
        ]}
      />
    </InlineFilterBar>
  );
};

export default OccupationalRiskFiltersComponent;
